<script setup lang="ts">
  import { computed, nextTick, watch } from 'vue';
  import type { DestinationPrices, SearchCriteriaHistory } from '@/entities/searchCriteria';
  import type { SearchFormGoal, SearchFormTracking } from '@/entities/searchForm';
  import { MobileAccountScreen } from '@/features/account';
  import { MobileHistoryScreen } from '@/features/searchHistory';
  import type { SwitchCity } from '@/features/switchCity';
  import { useFlipRouter } from '@/shared/composables';
  import { MobileMenuScreen } from '@/widgets/mobileMenu';
  import MobileCalendarScreen from './screens/MobileCalendarScreen.vue';
  import MobileDepartureScreen from './screens/MobileDepartureScreen.vue';
  import MobileDestinationScreen from './screens/MobileDestinationScreen.vue';
  import MobileMainScreen from './screens/MobileMainScreen.vue';
  import MobileNightsScreen from './screens/MobileNightsScreen.vue';
  import MobileTouristsScreen from './screens/MobileTouristsScreen.vue';
  import { type SearchFormCriteria } from '../../model/types';
  import { provideMobileSearchFlow, useMobileSearchFlow, type MobileScreen } from '../../model/useMobileSearchFlow';
  import { useSearchFormGoals } from '../../model/useSearchFormGoals';

  export interface SearchFormMobileFlowProps {
    modelValue: SearchFormCriteria;
    formUrl?: string;
    searchUrl?: string;
    resortsUrl?: string;
    accountUrl?: string;
    prices?: DestinationPrices;
    isVisibleDeparture?: boolean;
    isVisibleRoomButton?: boolean;
    historySearch?: SearchCriteriaHistory[];
    switchCities?: SwitchCity[];
    switchCurrentCity?: SwitchCity | null;
    searchBtnText?: string;
    dateFieldLabel?: string;
    nightsFieldLabel?: string;
    tracking?: SearchFormTracking;
  }

  const props = withDefaults(defineProps<SearchFormMobileFlowProps>(), {
    formUrl: '',
    searchUrl: '',
    resortsUrl: '',
    accountUrl: '',
    prices: undefined,
    isVisibleDeparture: true,
    isVisibleRoomButton: true,
    historySearch: () => [],
    switchCities: () => [],
    switchCurrentCity: null,
    searchBtnText: undefined,
    dateFieldLabel: undefined,
    nightsFieldLabel: undefined,
    tracking: undefined,
  });

  interface SearchFormMobileFlowEmits {
    (e: 'update:modelValue', value: SearchFormCriteria): void;
    (e: 'search', value: SearchFormCriteria): void;
    (e: 'change:city', city: SwitchCity): void;
    (e: 'chat'): void;
    (e: 'track', goal: SearchFormGoal): void;
  }

  const emit = defineEmits<SearchFormMobileFlowEmits>();
  const isLoading = computed(() => !props.modelValue.destination);
  const goals = useSearchFormGoals(props, (goal) => emit('track', goal), isLoading);

  const modelValue = computed<SearchFormCriteria>(() => props.modelValue);
  const prices = computed<DestinationPrices | undefined>(() => props.prices);
  const isVisibleDeparture = computed<boolean>(() => props.isVisibleDeparture);
  const isVisibleRoomButton = computed<boolean>(() => props.isVisibleRoomButton);
  const historySearch = computed<SearchCriteriaHistory[]>(() => props.historySearch);
  const searchBtnText = computed<string | undefined>(() => props.searchBtnText);
  const dateFieldLabel = computed<string | undefined>(() => props.dateFieldLabel);
  const nightsFieldLabel = computed<string | undefined>(() => props.nightsFieldLabel);
  const searchUrl = computed<string | undefined>(() => props.searchUrl);
  const resortsUrl = computed<string | undefined>(() => props.resortsUrl);

  const flip = useFlipRouter({
    routes: [
      { path: '/', name: 'main', component: MobileMainScreen },
      { path: '/departure', name: 'departure', component: MobileDepartureScreen },
      { path: '/destination', name: 'destination', component: MobileDestinationScreen },
      {
        path: '/history',
        name: 'history',
        component: MobileHistoryScreen,
        props: () => ({
          items: props.historySearch,
          embedded: true,
          onBack: () => flow.back(),
        }),
      },
      {
        path: '/menu',
        name: 'menu',
        component: MobileMenuScreen,
        edge: 'left',
        props: () => ({
          switchCities: props.switchCities,
          switchCurrentCity: props.switchCurrentCity,
          embedded: true,
          onBack: () => flow.back(),
          onOpenAccount: () => flow.open('account'),
          onChat: () => emit('chat'),
          onSwitchCity: (city: SwitchCity) => emit('change:city', city),
        }),
      },
      {
        path: '/account',
        name: 'account',
        component: MobileAccountScreen,
        props: () => ({
          embedded: true,
          accountUrl: props.accountUrl,
          onBack: () => flow.back(),
          onChat: () => emit('chat'),
        }),
      },
    ],
    initial: { name: 'main' },
  });

  let savedScroll = 0;

  const flow = useMobileSearchFlow({
    modelValue,
    formUrl: () => props.formUrl,
    searchUrl,
    resortsUrl,
    prices,
    isVisibleDeparture,
    isVisibleRoomButton,
    historySearch,
    embedded: true,
    nav: {
      open: (screen: MobileScreen) => {
        savedScroll = typeof window === 'undefined' ? 0 : window.scrollY;
        flip.push({ name: screen });
      },
      back: () => {
        flip.back();
        if (typeof window !== 'undefined' && savedScroll) {
          nextTick(() => window.scrollTo(0, savedScroll));
        }
      },
      replace: (screen: MobileScreen) => {
        flip.replace({ name: screen });
      },
    },
    onUpdate: (value) => emit('update:modelValue', value),
    onSearch: () => {
      goals.trackSearchClick();
      emit('search', props.modelValue);
    },
    searchBtnText,
    dateFieldLabel,
    nightsFieldLabel,
  });
  provideMobileSearchFlow(flow);

  const openHistory = (): void => flow.open('history');
  const openMenu = (): void => flow.open('menu');
  defineExpose({ openHistory, openMenu });

  watch(
    () => props.formUrl,
    () => {
      if (flip.route.value.name !== 'main') {
        flip.replace({ name: 'main' });
      }
    },
  );
</script>

<script lang="ts">
  export default {
    model: {
      prop: 'modelValue',
      event: 'update:modelValue',
    },
  };
</script>

<template>
  <div
    class="search-form-mobile-flow"
    data-testid="search-form-mobile"
  >
    <component
      :is="flip.RouterView"
      class="search-form-mobile-flow__stage"
    >
      <template #header>
        <slot name="header" />
      </template>
      <template #footer>
        <slot name="footer" />
      </template>
      <template #pageFooter>
        <slot name="pageFooter" />
      </template>
      <template #button-prepend>
        <slot name="button-prepend" />
      </template>
    </component>

    <MobileCalendarScreen v-if="flow.isCalendarOpen.value" />
    <MobileNightsScreen v-if="flow.isNightsOpen.value" />
    <MobileTouristsScreen v-if="flow.isTouristsOpen.value" />
  </div>
</template>

<style scoped lang="scss">
  .search-form-mobile-flow {
    box-sizing: border-box;
    display: flex;
    flex: 1 1 auto;
    flex-direction: column;
    width: 100%;

    &__stage {
      display: flex;
      flex: 1 1 auto;
      flex-direction: column;
    }
  }
</style>
